// Shuddhoshikha LMS - Model Test / Exam View Component
import { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Clock,
  ArrowLeft,
  Search,
  Award,
  HelpCircle,
  ChevronRight,
  Calendar
} from 'lucide-react';

interface Exam {
  id: number;
  title: string;
  duration_minutes: number;
  total_marks: number;
  negative_mark: number;
  academic_level: string;
  academic_group: string;
  start_time: string;
  end_time: string;
  status: 'ongoing' | 'upcoming' | 'completed';
  question_count: number;
  submissions: Array<{
    submission_id: number;
    score: number;
    correct: number;
    wrong: number;
    submitted_at: string;
  }>;
}

interface Question {
  id: number;
  question_text: string;
  options: string[];
  correct_answer?: string;
  explanation?: string;
  chosen?: string | null;
}

const toBengaliNumber = (num: number | string): string => {
  const bengaliDigits = ['০', '১', '২', '৩', '৪', '৫', '৬', '৭', '৮', '৯'];
  return num.toString().split('').map(digit => {
    return /^[0-9]$/.test(digit) ? bengaliDigits[parseInt(digit, 10)] || digit : digit;
  }).join('');
};

const formatBengaliDuration = (mins: number): string => {
  if (mins < 60) {
    return `${toBengaliNumber(mins)} মিনিট`;
  }
  const hrs = Math.floor(mins / 60);
  const remainingMins = mins % 60;
  if (remainingMins === 0) {
    return `${toBengaliNumber(hrs)} ঘণ্টা`;
  }
  return `${toBengaliNumber(hrs)} ঘণ্টা ${toBengaliNumber(remainingMins)} মিনিট`;
};

const formatBengaliDateTime = (dateTimeStr: string) => {
  if (!dateTimeStr) return { date: '', time: '' };
  try {
    const parts = dateTimeStr.split(' ');
    const datePart = parts[0] || '';
    const timePart = parts[1] || '';
    
    const dateObj = new Date(datePart + 'T' + (timePart || '00:00:00'));
    if (isNaN(dateObj.getTime())) {
       return { date: toBengaliNumber(datePart), time: toBengaliNumber(timePart.substring(0, 5)) };
    }
    
    const monthsBangla = [
      'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'
    ];
    const day = dateObj.getDate();
    const month = monthsBangla[dateObj.getMonth()];
    const year = dateObj.getFullYear();
    
    let hours = dateObj.getHours();
    const minutes = dateObj.getMinutes();
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    hours = hours ? hours : 12;
    const minutesStr = minutes < 10 ? '0' + minutes : minutes;
    
    const formattedDate = `${day} ${month} ${year}`;
    const formattedTime = `${hours}:${minutesStr} ${ampm}`;
    
    return {
      date: toBengaliNumber(formattedDate),
      time: toBengaliNumber(formattedTime)
    };
  } catch (err) {
    return { date: toBengaliNumber(dateTimeStr), time: '' };
  }
};

export default function ExamView({ onBack }: { onBack?: () => void }) {
  const [exams, setExams] = useState<Exam[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState<'all' | 'ongoing' | 'upcoming'>('all');
  const [searchQuery, setSearchQuery] = useState('');
  
  // Selected Exam states
  const [selectedExam, setSelectedExam] = useState<Exam | null>(null);
  const [leaderboard, setLeaderboard] = useState<Array<{ rank: number; name: string; score: number; institution?: string; avatar?: string }>>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [showLeaderboardModal, setShowLeaderboardModal] = useState(false);
  const [routinePdf, setRoutinePdf] = useState<string>((window as any).shAuth?.routine_pdf || "");

  // Active quiz environment states
  const [activeQuizQuestions, setActiveQuizQuestions] = useState<Question[]>([]);
  const [isTakingQuiz, setIsTakingQuiz] = useState(false);
  const [chosenAnswers, setChosenAnswers] = useState<Record<number, string>>({});
  const [remainingTime, setRemainingTime] = useState<number>(0);
  const timerRef = useRef<any>(null);

  // Result screen states
  const [quizResult, setQuizResult] = useState<{
    final_score: number;
    correct_count: number;
    wrong_count: number;
    unanswered_count: number;
    total_questions: number;
    questions: Question[];
  } | null>(null);

  const shAuth = (window as any).shAuth;
  const restUrl = shAuth?.rest_url || '/wp-json';
  const userClass = shAuth?.user?.class || 'all';
  const userGroup = shAuth?.user?.group || 'all';

  // 1. Fetch Exams
  const fetchExams = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${restUrl}/shuddhoshikha/v1/model-tests?level=${userClass}&group=${userGroup}`);
      const data = await res.json();
      if (data.success) {
        if (data.exams) setExams(data.exams);
        if (data.routine_pdf) setRoutinePdf(data.routine_pdf);
      }
    } catch (e) {
      console.error('Failed to load exams', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchExams();
  }, [userClass, userGroup]);

  // 2. Fetch Leaderboard & Details for an exam
  const handleSelectExam = async (exam: Exam) => {
    setSelectedExam(exam);
    setLoadingDetails(true);
    setQuizResult(null);
    try {
      const res = await fetch(`${restUrl}/shuddhoshikha/v1/model-tests/${exam.id}/leaderboard`);
      const data = await res.json();
      if (data.success && data.rankings) {
        setLeaderboard(data.rankings);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDetails(false);
    }
  };

  // 3. Start Exam & Persist countdown
  const handleStartExam = async (exam: Exam) => {
    try {
      setLoadingDetails(true);
      const res = await fetch(`${restUrl}/shuddhoshikha/v1/model-tests/${exam.id}/take`);
      const data = await res.json();
      if (data.success && data.questions) {
        setActiveQuizQuestions(data.questions);
        setChosenAnswers({});
        setQuizResult(null);
        setIsTakingQuiz(true);

        // Setup persistent refresh-proof timer
        const storageKey = `exam_start_${exam.id}`;
        let startTimestamp = localStorage.getItem(storageKey);
        const now = Math.floor(Date.now() / 1000);

        if (!startTimestamp) {
          localStorage.setItem(storageKey, now.toString());
          startTimestamp = now.toString();
        }

        const elapsed = now - parseInt(startTimestamp, 10);
        const durationSeconds = exam.duration_minutes * 60;
        const initialRemaining = durationSeconds - elapsed;

        if (initialRemaining <= 0) {
          alert('পরীক্ষার নির্ধারিত সময় শেষ হয়ে গেছে!');
          handleAutoSubmit(exam.id, {});
        } else {
          setRemainingTime(initialRemaining);
        }
      }
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingDetails(false);
    }
  };

  // Timer loop
  useEffect(() => {
    if (isTakingQuiz && remainingTime > 0) {
      timerRef.current = setInterval(() => {
        setRemainingTime(prev => {
          if (prev <= 1) {
            if (timerRef.current) clearInterval(timerRef.current);
            // Time is up: Auto-submit
            handleAutoSubmit(selectedExam!.id, chosenAnswers);
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }

    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [isTakingQuiz, remainingTime, chosenAnswers, selectedExam]);

  // Submit action helper
  const handleAutoSubmit = async (examId: number, answers: Record<number, string>) => {
    if (timerRef.current) clearInterval(timerRef.current);
    localStorage.removeItem(`exam_start_${examId}`);
    try {
      const res = await fetch(`${restUrl}/shuddhoshikha/v1/model-tests/${examId}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ answers })
      });
      const data = await res.json();
      if (data.success) {
        setQuizResult(data);
        setIsTakingQuiz(false);
        fetchExams(); // reload submissions stats
      }
    } catch (e) {
      console.error(e);
    }
  };

  const handleManualSubmit = () => {
    if (window.confirm('আপনি কি নিশ্চিত যে আপনার উত্তরপত্র জমা দিতে চান?')) {
      handleAutoSubmit(selectedExam!.id, chosenAnswers);
    }
  };

  const formatTimer = (seconds: number) => {
    const min = Math.floor(seconds / 60);
    const sec = seconds % 60;
    return `${min < 10 ? '0' + min : min}:${sec < 10 ? '0' + sec : sec}`;
  };

  // Filters
  const filteredExams = exams.filter(exam => {
    const matchesTab = activeTab === 'all' || exam.status === activeTab;
    const matchesSearch = exam.title.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesTab && matchesSearch;
  });

  const getOptionLetter = (idx: number): string => {
    const letters = ['ক', 'খ', 'গ', 'ঘ'];
    return letters[idx] || '';
  };

  return (
    <div className="max-w-7xl mx-auto px-4 py-6">
      <AnimatePresence mode="wait">
        
        {/* VIEW 1: ACTIVE QUIZ TAKING ENVIRONMENT */}
        {isTakingQuiz && selectedExam && (
          <motion.div
            key="taking-quiz"
            initial={{ opacity: 0, y: 15 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -15 }}
            className="space-y-6 pb-32"
          >
            {/* Top Navigation Header */}
            <div className="flex items-center justify-between bg-white dark:bg-[#1E1E2F] border border-gray-100 dark:border-slate-800 rounded-3xl p-4 shadow-sm">
              <button
                onClick={() => {
                  if (window.confirm('পরীক্ষা চলাকালীন পেছনে গেলে আপনার পরীক্ষা বাতিল হতে পারে। আপনি কি নিশ্চিত যে পেছনে যেতে চান?')) {
                    setIsTakingQuiz(false);
                    setSelectedExam(null);
                    if (timerRef.current) clearInterval(timerRef.current);
                    localStorage.removeItem(`exam_start_${selectedExam.id}`);
                  }
                }}
                className="flex items-center gap-2 text-slate-800 dark:text-white hover:text-[#00C9A7] dark:hover:text-[#00C9A7] transition-colors cursor-pointer"
              >
                <ArrowLeft className="w-5 h-5 font-black" />
                <span className="font-bangla text-base font-black">{selectedExam.title}</span>
              </button>
              
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-orange-50 dark:bg-orange-500/10 text-orange-600 dark:text-orange-400 text-xs font-bold font-english">
                  🔥 <span>১</span>
                </div>
                <div className="flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-amber-50 dark:bg-amber-500/10 text-amber-600 dark:text-amber-400 text-xs font-bold font-bangla">
                  ⭐ <span>০ পয়েন্ট</span>
                </div>
              </div>
            </div>

            {/* Exam Description Center Info */}
            <div className="text-center space-y-1">
              <p className="font-bangla text-sm text-slate-500 dark:text-slate-400">
                সময়ঃ <span className="font-bold">{formatBengaliDuration(selectedExam.duration_minutes)}</span>
              </p>
              <p className="font-bangla text-[11px] text-slate-400 dark:text-slate-500">
                প্রতি প্রশ্নের পূর্ণমান প্রশ্নের পাশে লেখা আছে এবং ভুলপ্রতি {toBengaliNumber(Math.round(selectedExam.negative_mark * 100))}% মার্কস কাটা যাবে
              </p>
            </div>

            {/* Questions Grid */}
            <div className="space-y-4 pb-28">
              {activeQuizQuestions.map((q, idx) => (
                <div key={q.id} className="bg-white dark:bg-[#1E1E2F] border border-gray-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <h3 className="font-bangla text-base font-bold text-slate-800 dark:text-white flex gap-2">
                      <span>{toBengaliNumber(idx + 1)}.</span>
                      <span>{q.question_text}</span>
                    </h3>
                    <div className="flex items-center gap-2 shrink-0">
                      <span className="bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-400 text-xs font-black px-2 py-1 rounded-lg">
                        {toBengaliNumber(1)}
                      </span>
                      <button className="p-1.5 text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 transition-colors">
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M5 5a2 2 0 012-2h10a2 2 0 012 2v16l-7-3.5L5 21V5z" />
                        </svg>
                      </button>
                    </div>
                  </div>

                  {/* Options Grid */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mt-4">
                    {q.options.map((opt, optIdx) => {
                      const letter = getOptionLetter(optIdx);
                      const isSelected = chosenAnswers[q.id] === letter;
                      return (
                        <button
                          key={optIdx}
                          onClick={() => setChosenAnswers(prev => ({ ...prev, [q.id]: letter }))}
                          className={`flex items-center gap-3.5 px-5 py-4 rounded-2xl border text-left font-bangla text-sm transition-all duration-200 cursor-pointer ${
                            isSelected
                              ? 'border-[#00C9A7] bg-[#e9fef8] dark:bg-[#00C9A7]/10 text-[#00b496] dark:text-[#00C9A7] font-bold shadow-md shadow-emerald-500/5'
                              : 'border-slate-150 dark:border-slate-800 hover:border-slate-350 dark:hover:border-slate-700 bg-white dark:bg-[#1E1E2F] text-slate-700 dark:text-[#E8EAED]'
                          }`}
                        >
                          <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold transition-all shrink-0 ${
                            isSelected
                              ? 'bg-[#00C9A7] text-white'
                              : 'bg-slate-100 dark:bg-slate-850 border border-slate-200 dark:border-slate-750 text-slate-500 dark:text-slate-400'
                          }`}>
                            {letter}
                          </span>
                          <span className="flex-1">{opt}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>

            {/* Bottom Sticky Action Bar */}
            <div className="fixed bottom-0 left-0 lg:left-[280px] right-0 z-50 bg-[#0B5C43] text-white py-4 px-6 shadow-2xl flex items-center justify-between transition-all duration-200">
              <div className="flex items-center gap-2.5 font-english text-lg font-bold">
                <Clock className="w-5 h-5 animate-pulse text-emerald-300" />
                <span>{formatTimer(remainingTime)}</span>
              </div>
              
              <button
                onClick={handleManualSubmit}
                className="px-10 py-3 rounded-full bg-[#00C9A7] hover:bg-[#009a80] text-white font-bangla font-black text-sm active:scale-[0.98] transition-all cursor-pointer text-center shadow-lg shadow-emerald-500/20"
              >
                সাবমিট
              </button>
              
              <div className="font-bangla text-base font-bold text-white">
                {toBengaliNumber(Object.keys(chosenAnswers).length)} / {toBengaliNumber(activeQuizQuestions.length)}
              </div>
            </div>
          </motion.div>
        )}

        {/* VIEW 2: EVALUATION SOLUTIONS / RESULTS PAGE */}
        {quizResult && selectedExam && (
          <motion.div
            key="results"
            initial={{ opacity: 0, scale: 0.98 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6"
          >
            {/* Header & Back Buttons */}
            <div className="flex items-center justify-between">
              <button
                onClick={() => {
                  setQuizResult(null);
                  setSelectedExam(null);
                }}
                className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-[#1E1E2F] border border-gray-100 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:text-slate-800 transition-colors cursor-pointer"
              >
                <ArrowLeft className="w-4 h-4" />
                <span className="font-bangla text-xs font-bold">ড্যাশবোর্ডে ফিরে যান</span>
              </button>
            </div>

            {/* Scorecard Widget */}
            <div className="bg-white dark:bg-[#1E1E2F] border border-gray-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
              <div className="p-4 rounded-2xl bg-emerald-50/50 dark:bg-emerald-500/5">
                <p className="font-bangla text-xs text-emerald-600 dark:text-emerald-400 font-bold">প্রাপ্ত নম্বর</p>
                <h4 className="font-bangla text-3xl font-black text-emerald-500 mt-2">{toBengaliNumber(quizResult.final_score)}</h4>
              </div>
              <div className="p-4 rounded-2xl bg-blue-50/50 dark:bg-blue-500/5">
                <p className="font-bangla text-xs text-blue-600 dark:text-blue-400 font-bold">সঠিক উত্তর</p>
                <h4 className="font-bangla text-3xl font-black text-blue-500 mt-2">{toBengaliNumber(quizResult.correct_count)}</h4>
              </div>
              <div className="p-4 rounded-2xl bg-rose-50/50 dark:bg-rose-500/5">
                <p className="font-bangla text-xs text-rose-600 dark:text-rose-400 font-bold">ভুল উত্তর</p>
                <h4 className="font-bangla text-3xl font-black text-rose-500 mt-2">{toBengaliNumber(quizResult.wrong_count)}</h4>
              </div>
              <div className="p-4 rounded-2xl bg-slate-50/50 dark:bg-slate-500/5">
                <p className="font-bangla text-xs text-slate-600 dark:text-slate-400 font-bold">উত্তরহীন</p>
                <h4 className="font-bangla text-3xl font-black text-slate-500 mt-2">{toBengaliNumber(quizResult.unanswered_count)}</h4>
              </div>
            </div>

            {/* Solution List */}
            <div className="space-y-4">
              <h3 className="font-bangla text-lg font-black text-slate-800 dark:text-white">উত্তরপত্র ও বিস্তারিত ব্যাখ্যা</h3>
              {quizResult.questions.map((q, idx) => (
                <div key={q.id} className="bg-white dark:bg-[#1E1E2F] border border-gray-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                  <h4 className="font-bangla text-base font-bold text-slate-800 dark:text-white flex gap-2">
                    <span>{toBengaliNumber(idx + 1)}.</span>
                    <span>{q.question_text}</span>
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {q.options.map((opt, optIdx) => {
                      const letter = getOptionLetter(optIdx);
                      const isCorrect = q.correct_answer === letter;
                      const isChosen = q.chosen === letter;
                      
                      let btnStyle = 'border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-white/5 text-slate-700 dark:text-[#E8EAED]';
                      if (isCorrect) {
                        btnStyle = 'border-emerald-500 bg-emerald-50 text-emerald-700 dark:bg-emerald-500/5 dark:text-emerald-400 font-bold';
                      } else if (isChosen && !isCorrect) {
                        btnStyle = 'border-rose-500 bg-rose-50 text-rose-700 dark:bg-rose-500/5 dark:text-rose-400 font-bold';
                      }

                      return (
                        <div
                          key={optIdx}
                          className={`flex items-center gap-3.5 px-5 py-4 rounded-2xl border text-left font-bangla text-sm ${btnStyle}`}
                        >
                          <span className={`w-7 h-7 rounded-xl flex items-center justify-center text-xs font-bold ${
                            isCorrect ? 'bg-emerald-500 text-white' : isChosen ? 'bg-rose-500 text-white' : 'bg-white dark:bg-slate-800 border text-slate-400 dark:text-slate-500'
                          }`}>
                            {letter}
                          </span>
                          <span>{opt}</span>
                        </div>
                      );
                    })}
                  </div>
                  {q.explanation && (
                    <div className="p-4 bg-slate-50 dark:bg-white/5 rounded-2xl text-slate-500 dark:text-slate-400 font-bangla text-xs leading-relaxed mt-2 border-l-4 border-slate-300 dark:border-slate-700">
                      <span className="font-bold text-slate-700 dark:text-white block mb-1">💡 ব্যাখ্যাঃ</span>
                      {q.explanation}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </motion.div>
        )}

        {/* VIEW 3: SINGLE EXAM DETAILS & LEADERBOARD SUMMARY */}
        {selectedExam && !isTakingQuiz && !quizResult && (
          <motion.div
            key="exam-details"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            exit={{ opacity: 0, x: -20 }}
            className="space-y-6"
          >
            {/* Back Button */}
            <button
              onClick={() => setSelectedExam(null)}
              className="flex items-center gap-2 px-4 py-2 rounded-xl bg-white dark:bg-[#1E1E2F] border border-gray-100 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:text-slate-800 transition-colors cursor-pointer"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="font-bangla text-xs font-bold">পেছনে যান</span>
            </button>

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              
              {/* Left Details Block */}
              <div className="lg:col-span-2 space-y-6">
                {(() => {
                  const startParts = formatBengaliDateTime(selectedExam.start_time);
                  const endParts = formatBengaliDateTime(selectedExam.end_time);
                  return (
                    <div className="bg-white dark:bg-[#1E1E2F] border border-gray-100 dark:border-slate-800 rounded-3xl p-8 shadow-sm space-y-6">
                      {/* Schedule Header */}
                      <div className="flex items-center justify-center gap-2 text-slate-800 dark:text-white font-bangla font-black text-lg">
                        <span>🗓️</span>
                        <span>সময়সূচী</span>
                      </div>

                      {/* Date Columns */}
                      <div className="grid grid-cols-2 gap-6 border-b border-slate-100 dark:border-slate-800/80 pb-6 text-center">
                        <div className="space-y-1">
                          <p className="font-bangla text-base font-black text-slate-800 dark:text-white">
                            {startParts.date || toBengaliNumber(selectedExam.start_time.split(' ')[0] || '')}
                          </p>
                          <p className="font-bangla text-xs text-slate-400 dark:text-slate-500 font-bold">
                            {startParts.time || toBengaliNumber(selectedExam.start_time.split(' ')[1] || '')}
                          </p>
                        </div>
                        <div className="space-y-1 border-l border-slate-100 dark:border-slate-800/80">
                          <p className="font-bangla text-base font-black text-slate-800 dark:text-white">
                            {endParts.date || toBengaliNumber(selectedExam.end_time.split(' ')[0] || '')}
                          </p>
                          <p className="font-bangla text-xs text-slate-400 dark:text-slate-500 font-bold">
                            {endParts.time || toBengaliNumber(selectedExam.end_time.split(' ')[1] || '')}
                          </p>
                        </div>
                      </div>

                      {/* Info Pill Block */}
                      <div className="flex items-center justify-between bg-slate-50 dark:bg-white/5 border border-slate-100 dark:border-slate-800/50 p-4 rounded-2xl">
                        <div className="flex items-center gap-4 text-xs font-bangla text-slate-600 dark:text-slate-400">
                          <span className="flex items-center gap-1.5 font-bold">
                            <Clock className="w-4 h-4 text-slate-400" />
                            {formatBengaliDuration(selectedExam.duration_minutes)}
                          </span>
                          <span className="w-1.5 h-1.5 rounded-full bg-slate-300 dark:bg-slate-700" />
                          <span className="flex items-center gap-1.5 font-bold">
                            <HelpCircle className="w-4 h-4 text-slate-400" />
                            {toBengaliNumber(selectedExam.question_count)} প্রশ্ন
                          </span>
                        </div>
                        
                        <span className="px-3 py-1 rounded-full text-[10px] font-black tracking-wider bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400 uppercase">
                          {selectedExam.status === 'ongoing' ? 'Ongoing' : selectedExam.status === 'upcoming' ? 'Upcoming' : 'Closed'}
                        </span>
                      </div>

                      {/* Action Buttons */}
                      <div className="space-y-3 pt-2">
                        {selectedExam.status === 'ongoing' ? (
                          <button
                            onClick={() => handleStartExam(selectedExam)}
                            className="w-full py-4 rounded-2xl bg-[#006A4E] hover:bg-[#00523C] text-white font-bangla font-black text-sm active:scale-[0.98] transition-all cursor-pointer text-center shadow-lg shadow-emerald-500/15"
                          >
                            পরীক্ষা শুরু করো
                          </button>
                        ) : (
                          <div className="py-4 text-center rounded-2xl bg-slate-100 dark:bg-slate-800 text-slate-500 dark:text-slate-400 font-bangla text-xs font-bold">
                            {selectedExam.status === 'upcoming' ? 'পরীক্ষাটি এখনও শুরু হয়নি' : 'পরীক্ষাটির সময় শেষ হয়ে গেছে'}
                          </div>
                        )}

                        <button
                          onClick={() => setSelectedExam(null)}
                          className="w-full py-4 rounded-2xl border border-slate-200 dark:border-slate-850 hover:border-slate-350 dark:hover:border-slate-700 bg-white dark:bg-[#1E1E2F] text-slate-600 dark:text-slate-300 font-bangla font-black text-sm active:scale-[0.98] transition-all cursor-pointer text-center"
                        >
                          অন্যান্য পরীক্ষা
                        </button>
                      </div>
                    </div>
                  );
                })()}

                {/* Submissions History */}
                <div className="bg-white dark:bg-[#1E1E2F] border border-gray-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-4">
                  <h3 className="font-bangla text-base font-black text-slate-800 dark:text-white">পূর্বের প্রচেষ্টাসমূহ</h3>
                  {selectedExam.submissions.length === 0 ? (
                    <p className="font-bangla text-xs text-slate-400 dark:text-slate-500">আপনি এখনও এই পরীক্ষাটি দেননি।</p>
                  ) : (
                    <div className="space-y-3">
                      {selectedExam.submissions.map((sub, sIdx) => (
                        <div key={sub.submission_id} className="flex items-center justify-between p-4 bg-slate-50 dark:bg-white/5 rounded-2xl">
                          <div>
                            <p className="font-bangla text-sm font-bold text-slate-800 dark:text-white">প্রচেষ্টা {toBengaliNumber(sIdx + 1)}</p>
                            <p className="font-bangla text-[10px] text-slate-400 dark:text-slate-500 mt-0.5">{sub.submitted_at}</p>
                          </div>
                          <div className="text-right">
                            <span className="font-bangla text-sm font-black text-[#00C9A7] bg-[#e9fef8] px-3 py-1 rounded-xl">
                              {toBengaliNumber(sub.score)} নম্বর
                            </span>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              {/* Right Leaderboard widget */}
              <div className="bg-white dark:bg-[#1E1E2F] border border-gray-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm space-y-6">
                <div
                  className="flex items-center justify-between cursor-pointer hover:opacity-80 transition-opacity"
                  onClick={() => setShowLeaderboardModal(true)}
                >
                  <h3 className="font-bangla text-base font-black text-slate-800 dark:text-white flex items-center gap-2">
                    লিডারবোর্ড
                    <span className="text-[10px] bg-[#00C9A7]/10 text-[#00C9A7] px-2 py-0.5 rounded-full font-bold font-bangla">সব দেখুন</span>
                  </h3>
                  <Award className="w-5 h-5 text-[#00C9A7]" />
                </div>
                {loadingDetails ? (
                  <p className="font-bangla text-xs text-slate-400 animate-pulse">লিডারবোর্ড লোড হচ্ছে...</p>
                ) : leaderboard.length === 0 ? (
                  <p className="font-bangla text-xs text-slate-400">কোনো রেকর্ড পাওয়া যায়নি।</p>
                ) : (
                  <div className="space-y-4">
                    {leaderboard.map((row, rIdx) => (
                      <div key={rIdx} className="flex items-center justify-between p-3 rounded-2xl bg-slate-50/50 dark:bg-white/5 hover:bg-slate-50 transition-colors">
                        <div className="flex items-center gap-3">
                          <span className={`w-6 h-6 rounded-lg flex items-center justify-center text-xs font-black ${
                            row.rank === 1 ? 'bg-amber-100 text-amber-700' : row.rank === 2 ? 'bg-slate-100 text-slate-700' : 'bg-orange-100 text-orange-700'
                          }`}>
                            #{toBengaliNumber(row.rank)}
                          </span>
                          <span className="font-bangla text-xs font-bold text-slate-800 dark:text-white">{row.name}</span>
                        </div>
                        <span className="font-bangla text-xs font-extrabold text-[#00C9A7]">{toBengaliNumber(row.score)} pts</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </motion.div>
        )}

        {/* VIEW 4: MAIN EXAMS GRID & DASHBOARD */}
        {!selectedExam && !isTakingQuiz && !quizResult && (
          <motion.div
            key="dashboard"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="space-y-6 animate-fade-in"
          >
            {/* Back Button and Header */}
            {onBack && (
              <div className="flex items-center gap-3">
                <button
                  onClick={onBack}
                  className="p-2.5 rounded-xl bg-white dark:bg-[#1E1E2F] border border-gray-100 dark:border-slate-800 text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white transition-colors cursor-pointer flex items-center justify-center shadow-sm"
                >
                  <ArrowLeft className="w-4 h-4" />
                </button>
                <div>
                  <h3 className="font-bangla text-base font-black text-slate-850 dark:text-white">মডেল টেস্ট</h3>
                  <p className="font-bangla text-[10px] text-slate-450 dark:text-slate-500">আপনার জন্য নির্ধারিত মডেল টেস্ট সমূহ</p>
                </div>
              </div>
            )}

            {/* Search and Filters Bar */}
            <div className="bg-white dark:bg-[#1E1E2F] border border-gray-100 dark:border-slate-800 rounded-3xl p-4 shadow-sm space-y-4">
              {/* Search */}
              <div className="relative w-full">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 dark:text-slate-500" />
                <input
                  type="text"
                  placeholder="পরীক্ষা খুঁজুন..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  className="w-full pl-11 pr-4 py-2.5 rounded-2xl border border-slate-100 dark:border-slate-800 bg-slate-50/50 dark:bg-white/5 font-bangla text-xs placeholder:text-slate-400 dark:placeholder:text-slate-500 focus:outline-none focus:border-[#00C9A7] transition-all"
                />
              </div>

              {/* Filters & Routine Row */}
              <div className="flex items-center justify-between gap-4 flex-wrap">
                <div className="flex items-center gap-2 p-1.5 bg-slate-50 dark:bg-white/5 rounded-2xl flex-wrap">
                  {(['all', 'ongoing', 'upcoming'] as const).map(tab => (
                    <button
                      key={tab}
                      onClick={() => setActiveTab(tab)}
                      className={`px-4 py-2 rounded-xl font-bangla text-xs font-bold cursor-pointer transition-all ${
                        activeTab === tab
                          ? 'bg-slate-800 dark:bg-[#8AB4F8] text-white dark:text-[#121212] shadow-sm'
                          : 'text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-white'
                      }`}
                    >
                      {tab === 'all' ? 'সব' : tab === 'ongoing' ? 'চলতি' : 'আসন্ন'}
                    </button>
                  ))}
                </div>

                {/* Routine Buttons */}
                {routinePdf && (
                  <div className="flex items-center gap-2">
                    <a
                      href={routinePdf}
                      target="_blank"
                      rel="noreferrer"
                      className="flex items-center gap-2 px-4 py-2 rounded-xl bg-[#E8F0FE] hover:bg-[#D2E3FC] text-[#1A73E8] dark:bg-[#1A73E8]/10 dark:hover:bg-[#1A73E8]/20 dark:text-[#8AB4F8] font-bold text-xs transition-colors cursor-pointer"
                    >
                      <Calendar className="w-4 h-4" />
                      <span>Routine</span>
                    </a>
                    <a
                      href={routinePdf}
                      download
                      target="_blank"
                      rel="noreferrer"
                      className="w-8 h-8 rounded-full bg-[#E8F0FE] hover:bg-[#D2E3FC] text-[#1A73E8] dark:bg-[#1A73E8]/10 dark:hover:bg-[#1A73E8]/20 dark:text-[#8AB4F8] flex items-center justify-center transition-colors cursor-pointer"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4"></path>
                      </svg>
                    </a>
                  </div>
                )}
              </div>
            </div>

            {/* List Grid */}
            {loading ? (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 animate-pulse">
                {[1, 2, 3].map((n) => (
                  <div key={n} className="bg-white dark:bg-[#1E1E2F] border border-gray-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm flex flex-col justify-between h-[220px]">
                    <div>
                      <div className="flex items-center justify-between">
                        <div className="w-16 h-4 bg-slate-100 dark:bg-slate-800 rounded-lg" />
                        <div className="w-12 h-4 bg-slate-100 dark:bg-slate-800 rounded-lg" />
                      </div>
                      <div className="w-3/4 h-5 bg-slate-200 dark:bg-slate-750 rounded-lg mt-6" />
                      <div className="w-1/2 h-5 bg-slate-200 dark:bg-slate-750 rounded-lg mt-2" />
                      <div className="flex items-center gap-4 mt-6">
                        <div className="w-20 h-4 bg-slate-100 dark:bg-slate-800 rounded-lg" />
                        <div className="w-20 h-4 bg-slate-100 dark:bg-slate-800 rounded-lg" />
                      </div>
                    </div>
                    <div className="border-t border-slate-50 dark:border-slate-850/60 pt-4 mt-6 flex items-center justify-between">
                      <div className="w-28 h-4 bg-slate-105 dark:bg-slate-800 rounded-lg" />
                      <div className="w-16 h-4 bg-slate-205 dark:bg-slate-750 rounded-lg" />
                    </div>
                  </div>
                ))}
              </div>
            ) : filteredExams.length === 0 ? (
              <div className="bg-white dark:bg-[#1E1E2F] border border-gray-100 dark:border-slate-800 rounded-3xl p-12 text-center shadow-sm">
                <HelpCircle className="w-12 h-12 text-slate-300 dark:text-slate-700 mx-auto" />
                <h4 className="font-bangla text-base font-bold text-slate-800 dark:text-white mt-4">কোনো পরীক্ষা পাওয়া যায়নি</h4>
                <p className="font-bangla text-xs text-slate-450 mt-1">আপনার একাডেমিক লেভেল এবং গ্রুপের জন্য কোনো পরীক্ষা খালি নেই।</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                {filteredExams.map(exam => {
                  const isOngoing = exam.status === 'ongoing';
                  const isUpcoming = exam.status === 'upcoming';

                  let badgeColor = 'bg-slate-100 text-slate-600 dark:bg-slate-500/10 dark:text-slate-400';
                  if (isOngoing) badgeColor = 'bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-400 border border-emerald-500/15';
                  if (isUpcoming) badgeColor = 'bg-blue-50 text-blue-600 dark:bg-blue-500/10 dark:text-blue-400 border border-blue-500/15';

                  return (
                    <div
                      key={exam.id}
                      onClick={() => handleSelectExam(exam)}
                      className="bg-white dark:bg-[#1E1E2F] border border-gray-100 dark:border-slate-800 rounded-3xl p-6 shadow-sm hover:shadow-md transition-all cursor-pointer group flex flex-col justify-between"
                    >
                      <div>
                        {/* Status badge */}
                        <div className="flex items-center justify-between">
                          <span className={`px-2.5 py-0.5 rounded-lg text-[9px] font-black tracking-wider uppercase ${badgeColor}`}>
                            {exam.status === 'ongoing' ? 'Ongoing' : exam.status === 'upcoming' ? 'Upcoming' : 'Closed'}
                          </span>
                          <span className="font-bangla text-[10px] text-slate-400 dark:text-slate-500 flex items-center gap-1">
                            <Clock className="w-3.5 h-3.5" />
                            {toBengaliNumber(exam.duration_minutes)} মিনিট
                          </span>
                        </div>

                        {/* Title */}
                        <h3 className="font-bangla text-base font-extrabold text-slate-850 dark:text-white mt-4 group-hover:text-[#00C9A7] transition-colors leading-snug">
                          {exam.title}
                        </h3>

                        {/* Metadata counts */}
                        <div className="flex items-center gap-4 mt-5 text-[11px] font-bangla text-slate-455 dark:text-slate-500">
                          <span className="flex items-center gap-1.5"><HelpCircle className="w-3.5 h-3.5" /> {toBengaliNumber(exam.question_count)} টি প্রশ্ন</span>
                          <span className="flex items-center gap-1.5"><Award className="w-3.5 h-3.5" /> {toBengaliNumber(exam.total_marks)} পূর্ণমান</span>
                        </div>
                      </div>

                      {/* Card Action footer */}
                      <div className="border-t border-slate-50 dark:border-slate-800/60 pt-4 mt-6 flex items-center justify-between">
                        <span className="font-bangla text-[10px] text-slate-400 dark:text-slate-550">
                          {exam.submissions.length > 0 ? `${toBengaliNumber(exam.submissions.length)} টি চেষ্টা সম্পন্ন` : 'এখনও চেষ্টা করা হয়নি'}
                        </span>
                        <span className="text-[#00C9A7] font-bangla text-xs font-black flex items-center gap-1 group-hover:translate-x-0.5 transition-transform">
                          {isOngoing ? 'পরীক্ষা দিন' : 'বিস্তারিত দেখুন'}
                          <ChevronRight className="w-3.5 h-3.5" />
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {/* Premium Podium Leaderboard Modal */}
      {showLeaderboardModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-md animate-fade-in">
          <div className="bg-white dark:bg-[#1E1E2F] w-full max-w-2xl rounded-3xl shadow-2xl border border-slate-100 dark:border-slate-800 overflow-hidden flex flex-col max-h-[85vh] animate-scale-up">
            {/* Modal Header */}
            <div className="p-6 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between">
              <div>
                <h3 className="font-bangla text-lg font-black text-slate-850 dark:text-white">
                  {selectedExam?.title || "মডেল টেস্ট লিডারবোর্ড"}
                </h3>
                <p className="font-bangla text-xs text-slate-400 mt-1">পরীক্ষার্থীদের মেধা তালিকা ও প্রাপ্ত নম্বর</p>
              </div>
              <button
                onClick={() => setShowLeaderboardModal(false)}
                className="p-2 rounded-xl bg-slate-50 dark:bg-white/5 text-slate-500 hover:text-slate-855 dark:hover:text-white transition-colors cursor-pointer"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M6 18L18 6M6 6l12 12"></path>
                </svg>
              </button>
            </div>

            {/* Modal Content */}
            <div className="flex-1 overflow-y-auto p-6 space-y-8 custom-scrollbar">
              {/* Podium Section (Top 3 Candidates) */}
              {leaderboard.length >= 3 && (
                <div className="flex items-end justify-center gap-4 sm:gap-8 pt-6 pb-2">
                  {/* Rank #2 (Left) */}
                  {(() => {
                    const candidate = leaderboard.find(c => c.rank === 2) || leaderboard[1];
                    if (!candidate) return null;
                    return (
                      <div className="flex flex-col items-center text-center w-24 sm:w-28">
                        <div className="relative">
                          <div className="w-14 h-14 rounded-full border-2 border-slate-350 overflow-hidden bg-slate-105 flex items-center justify-center shadow-md">
                            {candidate.avatar ? (
                              <img src={candidate.avatar} alt={candidate.name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-slate-500 font-extrabold text-lg">{candidate.name.charAt(0)}</span>
                            )}
                          </div>
                          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-slate-400 text-white font-bold text-[10px] px-2 py-0.5 rounded-full shadow-sm">
                            #২
                          </span>
                        </div>
                        <p className="font-bangla text-xs font-black text-slate-800 dark:text-white mt-3 truncate w-full">{candidate.name}</p>
                        <p className="font-bangla text-[10px] font-extrabold text-[#00C9A7] mt-0.5">{toBengaliNumber(candidate.score)}</p>
                      </div>
                    );
                  })()}

                  {/* Rank #1 (Center) */}
                  {(() => {
                    const candidate = leaderboard.find(c => c.rank === 1) || leaderboard[0];
                    if (!candidate) return null;
                    return (
                      <div className="flex flex-col items-center text-center w-28 sm:w-32 -translate-y-2">
                        <div className="relative">
                          {/* Crown Icon */}
                          <div className="absolute -top-6 left-1/2 -translate-x-1/2 text-yellow-500 animate-bounce">
                            <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                              <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z"></path>
                            </svg>
                          </div>
                          <div className="w-18 h-18 rounded-full border-4 border-amber-400 overflow-hidden bg-slate-105 flex items-center justify-center shadow-lg">
                            {candidate.avatar ? (
                              <img src={candidate.avatar} alt={candidate.name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-amber-600 font-extrabold text-xl">{candidate.name.charAt(0)}</span>
                            )}
                          </div>
                          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-amber-400 text-white font-bold text-xs px-2.5 py-0.5 rounded-full shadow-sm">
                            #১
                          </span>
                        </div>
                        <p className="font-bangla text-sm font-black text-slate-800 dark:text-white mt-3 truncate w-full">{candidate.name}</p>
                        <p className="font-bangla text-xs font-black text-[#00C9A7] mt-0.5">{toBengaliNumber(candidate.score)}</p>
                      </div>
                    );
                  })()}

                  {/* Rank #3 (Right) */}
                  {(() => {
                    const candidate = leaderboard.find(c => c.rank === 3) || leaderboard[2];
                    if (!candidate) return null;
                    return (
                      <div className="flex flex-col items-center text-center w-24 sm:w-28">
                        <div className="relative">
                          <div className="w-14 h-14 rounded-full border-2 border-orange-300 overflow-hidden bg-slate-105 flex items-center justify-center shadow-md">
                            {candidate.avatar ? (
                              <img src={candidate.avatar} alt={candidate.name} className="w-full h-full object-cover" />
                            ) : (
                              <span className="text-orange-500 font-extrabold text-lg">{candidate.name.charAt(0)}</span>
                            )}
                          </div>
                          <span className="absolute -bottom-1 left-1/2 -translate-x-1/2 bg-orange-400 text-white font-bold text-[10px] px-2 py-0.5 rounded-full shadow-sm">
                            #৩
                          </span>
                        </div>
                        <p className="font-bangla text-xs font-black text-slate-800 dark:text-white mt-3 truncate w-full">{candidate.name}</p>
                        <p className="font-bangla text-[10px] font-extrabold text-[#00C9A7] mt-0.5">{toBengaliNumber(candidate.score)}</p>
                      </div>
                    );
                  })()}
                </div>
              )}

              {/* Ranks List (#4 onwards or all if < 3) */}
              <div className="space-y-3.5">
                {leaderboard.map((row) => {
                  // Skip top 3 if we render podium
                  if (leaderboard.length >= 3 && row.rank <= 3) return null;
                  return (
                    <div
                      key={row.rank}
                      className="flex items-center justify-between p-4 bg-slate-50/50 dark:bg-white/5 hover:bg-slate-50 dark:hover:bg-white/10 rounded-2xl transition-all border border-slate-100/50 dark:border-slate-800/40"
                    >
                      <div className="flex items-center gap-4">
                        <span className="font-bangla text-xs font-black text-slate-400 dark:text-slate-500 w-8">
                          #{toBengaliNumber(row.rank)}
                        </span>
                        <div className="w-10 h-10 rounded-full overflow-hidden bg-slate-100 flex items-center justify-center border border-slate-100 dark:border-slate-800">
                          {row.avatar ? (
                            <img src={row.avatar} alt={row.name} className="w-full h-full object-cover" />
                          ) : (
                            <span className="text-slate-500 font-bold text-xs">{row.name.charAt(0)}</span>
                          )}
                        </div>
                        <div>
                          <p className="font-bangla text-xs font-bold text-slate-850 dark:text-white">{row.name}</p>
                          {row.institution && (
                            <p className="font-bangla text-[10px] text-slate-450 dark:text-slate-500 mt-0.5">{row.institution}</p>
                          )}
                        </div>
                      </div>
                      <span className="font-bangla text-xs font-extrabold text-[#00C9A7] bg-[#e9fef8] dark:bg-[#00C9A7]/10 px-3 py-1 rounded-xl">
                        {toBengaliNumber(row.score)}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
